import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { User, UserRoles } from 'src/common/models/user';
import { MenuOption } from 'src/components/Sidebar/types';
import { RootState } from 'src/state/reducer';
import { USER_SCOPES, UserPermission } from './UserPermission';
import { useNavigate } from 'react-router';
import { Urls } from 'src/common/urls';

interface ReduxProps {
    selectedMenuOption?: MenuOption
    user?: User
}

interface UserPermissionData {
    isUserAllowedInScreen: boolean
    isUserAllowedToPerformAdminActions: boolean
    userRolePermissions: string[]
    validateUserScope: (scope: string) => boolean
    validateUserScopeForPage: (scope: string) => void
}

const useUserPermission = (): UserPermissionData => {
    const userPermissions = new UserPermission()
    const navigate = useNavigate()
    const [isUserAllowed, setIsUserAllowed] = useState<boolean>(false);
    const [isUserAllowedAdminActions, setIsUserAllowedAdminActions] = useState<boolean>(false);
    const [userRolePermissions, setUserRolePermissions] = useState<string[]>([])

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        selectedMenuOption: state.menu.selectedMenuOption,
        user: state.security.user,
    }))

    const getUserScreenPermissions = (): boolean => {
        const roles: UserRoles[] = reduxProps.selectedMenuOption?.roles || []

        return (
            roles.includes(reduxProps.user?.userRoleId as any) ||
            roles.includes(UserRoles.ALL)
        ) || false
    }

    const getUserAdminPermissions = (): boolean => {
        const rolesWithAdminPermissions: UserRoles[] = [
            UserRoles.ADMIN,
        ]

        return rolesWithAdminPermissions.includes(reduxProps.user?.userRoleId as any)
    }

    const getUserRolePermissions = (userRoleId: number): string[] => {
        return userPermissions.getUserPermissions(userRoleId)
    }

    const validateUserScope = (scope: string): boolean => {
        if (!reduxProps.user?.userRoleId) {
            return false
        }

        const permissions = userPermissions.getUserPermissions(reduxProps.user?.userRoleId)
        return permissions.includes(scope)
    }

    const validateUserScopeForPage = (scope: string) => {
        if (!reduxProps.user?.userRoleId) {
            return
        }

        const permissions = userPermissions.getUserPermissions(reduxProps.user?.userRoleId)
        if (!permissions.includes(scope) && !permissions.includes(USER_SCOPES.ALL)) {
            navigate(Urls.FRONTEND.HOME)
        }
    }

    useEffect(() => {
        if (reduxProps.user) {
            setIsUserAllowed(getUserScreenPermissions());
            setIsUserAllowedAdminActions(getUserAdminPermissions());
            setUserRolePermissions(getUserRolePermissions(reduxProps.user.userRoleId))
        }
    }, [reduxProps.user]);

    return {
        isUserAllowedInScreen: isUserAllowed,
        isUserAllowedToPerformAdminActions: isUserAllowedAdminActions,
        userRolePermissions,
        validateUserScope,
        validateUserScopeForPage,
    }
};

export default useUserPermission;
